/**
 * Asking the worker for a move.
 *
 * Two things this has to get right, and they are the two things a naive version gets
 * wrong. A reply that arrives for a position the game has already left must be ignored,
 * or taking a move back makes the bot answer a question nobody asked any more. And the
 * bot must not answer *instantly*: a search that finishes in 40ms lands before the board
 * has finished drawing your own move, which reads as a glitch rather than as a fast
 * opponent.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Move, Variant } from '@gok/engine';
import type { LevelId } from '@gok/bot';

import { type BotReply, isFailure } from './protocol.ts';

/**
 * The shortest a move is allowed to take.
 *
 * Long enough that a move reads as a decision rather than a lookup, short enough that
 * nobody is left waiting on an animation. The easiest level searches for 80ms, so without
 * this it would move before the board had settled.
 */
const MIN_THINK_MS = 320;

export interface Thought extends BotReply {
  /** How long the page waited, including the pause above. */
  readonly waitedMs: number;
}

export interface BotHandle {
  /** Resolves with the bot's move, or rejects if the worker could not produce one. */
  think(variant: Variant, moves: readonly Move[], level: LevelId): Promise<Thought>;
  /** Abandon whatever is in flight. Its reply, if it arrives, is dropped. */
  cancel(): void;
  thinking: boolean;
}

interface Pending {
  readonly id: number;
  readonly startedAt: number;
  resolve(thought: Thought): void;
  reject(error: Error): void;
}

/**
 * @param minThinkMs how long a move must take before it is shown. The default suits a
 * human playing an opponent; two bots playing each other have nobody waiting on a move of
 * their own, so a floor there is delay and nothing else.
 */
export function useBot(minThinkMs: number = MIN_THINK_MS): BotHandle {
  const workerRef = useRef<Worker | null>(null);
  // Read through a ref so that changing the pace does not rebuild the worker and throw
  // away every transposition table it is holding.
  const floorRef = useRef(minThinkMs);
  floorRef.current = minThinkMs;
  const pendingRef = useRef<Pending | null>(null);
  const nextId = useRef(1);
  const [thinking, setThinking] = useState(false);

  useEffect(() => {
    const worker = new Worker(new URL('./worker.ts', import.meta.url), {
      type: 'module',
    });

    worker.onmessage = (event) => {
      const message = event.data;
      const pending = pendingRef.current;
      // A reply for a request nobody is waiting on any more. This is the normal case
      // after a take-back, not an error.
      if (!pending || pending.id !== message.id) return;

      pendingRef.current = null;
      setThinking(false);

      if (isFailure(message)) {
        pending.reject(new Error(message.error));
        return;
      }

      const waitedMs = performance.now() - pending.startedAt;
      const rest = Math.max(0, floorRef.current - waitedMs);
      setTimeout(
        () => pending.resolve({ ...message, waitedMs: waitedMs + rest }),
        rest,
      );
    };

    worker.onerror = (event) => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      setThinking(false);
      pending?.reject(new Error(event.message || 'the bot stopped working'));
    };

    workerRef.current = worker;
    return () => {
      worker.terminate();
      workerRef.current = null;
      pendingRef.current = null;
    };
  }, []);

  const cancel = useCallback(() => {
    pendingRef.current = null;
    setThinking(false);
  }, []);

  const think = useCallback(
    (variant: Variant, moves: readonly Move[], level: LevelId) =>
      new Promise<Thought>((resolve, reject) => {
        const worker = workerRef.current;
        if (!worker) {
          reject(new Error('the bot is not running'));
          return;
        }

        const id = nextId.current++;
        pendingRef.current = { id, startedAt: performance.now(), resolve, reject };
        setThinking(true);
        worker.postMessage({ id, variant, moves, level });
      }),
    [],
  );

  return { think, cancel, thinking };
}
